import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const heroImage = "https://images.unsplash.com/photo-1745970347652-8f22f5d7d3ba";

  // Handle scroll for header transparency
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      emoji: "🏗️",
      question: "What exactly is the Digital Advancement Accelerator?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            The Accelerator is a 10-week, one-on-one engagement where our senior strategists, designers, and technologists become your executive build team — not just advisors.
          </p>
          <p className="body-medium text-charcoal">We work alongside you to:</p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Design your business model
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Build your systems
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Create your brand presence
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Launch your scalable offers
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Train your own AI business co-pilot
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            It's not a course or cohort — it's hands-on execution to transform your business from founder-dependent to scalable.
          </p>
        </div>
      )
    },
    {
      emoji: "⚖️",
      question: "How is this different from hiring an agency or coach?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            Coaches give advice. Agencies build what they're told.<br />
            We do both — but better.
          </p>
          <p className="body-medium text-charcoal">
            We architect your strategy and build the brand, systems, tech, and AI to make it real.
          </p>
          <p className="body-medium text-charcoal">
            You're not just getting a logo or a funnel — you're getting an entire business infrastructure built around your DNA, offers, and voice.
          </p>
          <p className="body-medium text-charcoal font-semibold">
            No templates. No one-size-fits-all. All custom.
          </p>
        </div>
      )
    },
    {
      emoji: "👤",
      question: "Who is this designed for?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            This was built for ambitious founders ready to stop being the whole business.
          </p>
          <p className="body-medium text-charcoal">We work best with:</p>
          <ul className="space-y-3">
            <li className="body-medium text-charcoal">
              <span className="text-bronze font-semibold">Tech Micro-Founders</span> — SaaS, AI, or B2B startups moving from product tinkering to market traction
            </li>
            <li className="body-medium text-charcoal">
              <span className="text-bronze font-semibold">Coaches/Consultants</span> — Experts with proven client results who need systems, branding, and scalable offerings
            </li>
            <li className="body-medium text-charcoal">
              <span className="text-bronze font-semibold">Creators & Ecom Founders</span> — Personal brands or product founders ready to turn their hustle into a real company
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            If you have skill, audience, or product — but lack structure, systems, and time — you're our person.
          </p>
        </div>
      )
    },
    {
      emoji: "💵",
      question: "Do I need to be making a certain amount of revenue first?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            No. You don't need a specific revenue level — you need:
          </p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              A validated idea, offer, or audience
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Financial readiness to invest $25K
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Commitment to actively build with us for 10 weeks
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            We've helped founders at $0, $50K, and $250K+. The key is being ready to scale.
          </p>
        </div>
      )
    },
    {
      emoji: "⏳",
      question: "How much time will I need each week?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            Expect 3–4 hours per week, usually:
          </p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              One 90-minute strategy or build session
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              1–2 hours of focused work or approvals
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            We do the heavy lifting — you focus on insight, decisions, and vision.
          </p>
        </div>
      )
    },
    {
      emoji: "📦",
      question: "Will I get actual deliverables or just advice?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            Deliverables are the heart of this accelerator.
          </p>
          <p className="body-medium text-charcoal">You will leave with:</p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Full brand identity + site (1–5 pages)
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Sales funnel and marketing automations
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              CRM and onboarding system
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Offer suite and pricing model
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Growth roadmap and data room
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              A custom AI co-pilot trained on your business
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              SOPs, dashboards, and training to run it all
            </li>
          </ul>
          <p className="body-medium text-charcoal font-semibold">
            This is a business built for you, with you.
          </p>
        </div>
      )
    },
    {
      emoji: "🤖",
      question: "How does the AI co-pilot work?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            We build and train a private GPT-based assistant that knows your offers, workflows, and voice. It can:
          </p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Draft emails, posts, proposals, and reports
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Answer client or customer FAQs
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Analyze data, summarize documents, or generate marketing content
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            You'll also get a prompt library to get high-quality output quickly.<br />
            <strong>Plus:</strong> 6 months of free hosting for your co-pilot after the accelerator.
          </p>
        </div>
      )
    },
    {
      emoji: "🧩",
      question: "What if I don't have a team yet?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            Perfect. This is built to replace the need for a big team early on.
          </p>
          <p className="body-medium text-charcoal">We'll:</p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Automate the tasks you'd normally hire for (admin, marketing, support)
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Give you SOPs and dashboards to plug in future hires later
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            Most of our founders start solo — and leave finally feeling like they have leverage.
          </p>
        </div>
      )
    },
    {
      emoji: "🛍️",
      question: "Will this work for B2C founders like me, or is it just for startups?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            Absolutely. About half our founders are B2C — creators, ecom founders, trainers, stylists, and coaches.
          </p>
          <p className="body-medium text-charcoal">We'll help you:</p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Professionalize your brand
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Automate your content and shop systems
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Turn your one-person hustle into a scalable company
            </li>
          </ul>
        </div>
      )
    },
    {
      emoji: "🏁",
      question: "What happens after the 10 weeks?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">You walk away with:</p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              Your entire business system running
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              All assets, SOPs, and logins handed over
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              A 90-day growth roadmap
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-teal mr-3">✓</span>
              6 months of AI co-pilot hosting included
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            If you want ongoing help, you can extend with our fractional CTO/COO team — totally optional.
          </p>
        </div>
      )
    },
    {
      emoji: "💰",
      question: "What's the investment?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">
            The Accelerator is a $25,000 engagement.
          </p>
          <p className="body-medium text-charcoal">
            We also offer founder-friendly payment plans to spread your investment across milestones.
          </p>
          <p className="body-medium text-charcoal">
            On your strategy call, we'll walk you through ROI projections for your business.
          </p>
        </div>
      )
    },
    {
      emoji: "📅",
      question: "How do I start?",
      answer: (
        <div className="space-y-4">
          <p className="body-medium text-charcoal">It's simple:</p>
          <ul className="space-y-2">
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Book your private strategy call
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              We review where you are and what your build would include
            </li>
            <li className="body-medium text-charcoal flex items-start">
              <span className="text-bronze mr-3">•</span>
              Reserve your build window (usually 2 weeks out)
            </li>
          </ul>
          <p className="body-medium text-charcoal">
            No application. No cohorts. Start anytime.
          </p>
          <div className="mt-6">
            <a href="/book-call">
              <Button className="btn-primary">
                Book Your Strategy Call
              </Button>
            </a>
          </div>
        </div>
      )
    }
  ];

  // Removed roadmapSteps array - replaced with simplified process in How Advance Works section

  const testimonials = [
    {
      founder: "Jordan Reyes",
      company: "DevNest",
      location: "Austin, TX",
      quote: "Advance gave me the infrastructure and clarity I didn't realize I was missing. Now I can focus on building the product instead of putting out fires.",
      result: "Closed $60K ARR within 45 days",
      type: "Tech Micro-Founder"
    },
    {
      founder: "Maya Thompson", 
      company: "Elevare Leadership",
      location: "Atlanta, GA",
      quote: "Advance helped me step out of the weeds and into my role as CEO. My business finally works like the premium experience I deliver.",
      result: "65% revenue increase, 40% less delivery time",
      type: "Coach/Consultant"
    },
    {
      founder: "Alex Kim",
      company: "Studio Sol", 
      location: "Denver, CO",
      quote: "Advance turned my chaos into clarity. I finally have space to be creative again — and my business is scaling faster than ever.",
      result: "Doubled revenue in 2 months, 70% fewer manual tasks",
      type: "Creator/E-commerce"
    }
  ];

  const pricingOptions = [
    {
      title: "Investment Build — Best Value",
      price: "$22,500 (One-Time Payment)",
      savings: "Save $2,500 compared to other plans",
      description: "Complete 10-week Accelerator",
      features: [
        "Priority Start: Begin in 7 days (vs. 14)",
        "Extended ROI: +3 months AI Co-Pilot hosting", 
        "Executive Edge: Investor deck polish + pitch coaching"
      ],
      tagline: "For leaders who want maximum momentum and immediate ROI.",
      highlight: true,
      icon: "🚀"
    },
    {
      title: "Milestone Build — Balanced", 
      price: "2 Payments of $12,500 (Total $25,000)",
      description: "Aligned to key project milestones",
      features: [
        "Complete 10-week Accelerator",
        "50% due at kickoff, 50% due at Week 6",
        "Strategic Bonus: Professional brand photography kit"
      ],
      tagline: "For executives who prefer milestone-based capital deployment.",
      icon: "⚡"
    },
    {
      title: "Installment Build — Flexible",
      price: "4 Payments of $7,000 (Total $28,000)", 
      description: "Distributed across 10 weeks",
      features: [
        "Complete 10-week Accelerator",
        "Lower entry payments, spread out",
        "Strategic Bonus: Post-engagement strategy office hours"
      ],
      tagline: "For owners balancing cash flow while still securing the full build.",
      icon: "📈"
    },
    {
      title: "Strategic Diagnostic — Starter Path",
      price: "$5,000 Flat Fee",
      description: "Covers Weeks 1–3 only (Vision, Business Model, Customer Journey)",
      features: [
        "Tangible strategic deliverables",
        "Pauses until remaining $20,000 is invested",
        "Credit: $5,000 fully applied if you continue"
      ],
      tagline: "For founders validating fit or preparing for outside funding.",
      icon: "⏳"
    }
  ];

  return (
    <div className="homepage bg-ivory">
      {/* Header */}
      <header className={`fixed top-0 w-full z-50 nav-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container mx-auto px-8 py-6 flex justify-between items-center">
          <div className="text-charcoal">
            <span className="headline-small" style={{ fontFamily: 'Canela, Georgia, serif' }}>ADVANCE</span>
            <span className="label-text text-graphite ml-3">by Copper & Vine</span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="/quiz" className="nav-link">Readiness Quiz</a>
          </nav>
          <div className="flex items-center">
            <a href="/get-started">
              <Button className="btn-primary">
                Get Started
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src={heroImage} 
            alt="Executive workspace" 
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          <div className="hero-content">
            <h1 className="headline-large mb-8">
              We don't coach founders — we build companies.
            </h1>
            <p className="body-large text-graphite mb-12 max-w-xl">
              Advance is a 10-week, done-with-you build designed for founders and leaders who are ready to stop carrying the business alone and put systems, automation, and structure in place to grow sustainably.
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <a href="/quiz">
                <Button className="btn-primary">
                  Start with the 5-minute readiness check
                </Button>
              </a>
              <button 
                onClick={() => {
                  document.querySelector('#how-advance-works')?.scrollIntoView({ 
                    behavior: 'smooth' 
                  });
                }}
              >
                <Button variant="outline" className="btn-secondary">
                  How it Works
                </Button>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What This Is */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="headline-medium mb-8">
              Not a program. A build team.
            </h2>
            <p className="body-large text-graphite mb-16 max-w-3xl mx-auto">
              Advance by Copper & Vine is a private 10-week engagement where our senior strategists, designers, and technologists become your executive build team. We don't coach from the sidelines — we build alongside you, compressing years of trial-and-error into weeks of structured momentum.
            </p>
            
            <div className="grid md:grid-cols-3 gap-12 mt-20">
              <div className="text-center">
                <div className="w-20 h-20 bg-bronze rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl">
                  ✓
                </div>
                <h3 className="subhead-medium text-charcoal mb-4">Done-with-you execution</h3>
                <p className="body-medium text-graphite">We build, not just advise</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-bronze rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl">
                  ∞
                </div>
                <h3 className="subhead-medium text-charcoal mb-4">Founder-paced</h3>
                <p className="body-medium text-graphite">Start anytime, no cohorts or deadlines</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-bronze rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl">
                  🚀
                </div>
                <h3 className="subhead-medium text-charcoal mb-4">Outcome-driven</h3>
                <p className="body-medium text-graphite">Launch with a full business, not just a plan</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="section-padding bg-ivory">
        <div className="container mx-auto">
          <h2 className="headline-medium text-center mb-12">
            For leaders at an inflection point
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <ul className="space-y-6">
              <li className="body-large flex items-start">
                <span className="text-bronze mr-4">•</span>
                <span>Founders moving beyond proof of concept and needing structure for scale</span>
              </li>
              <li className="body-large flex items-start">
                <span className="text-bronze mr-4">•</span>
                <span>Solo operators who want to shift from doing everything themselves to leading a business with leverage</span>
              </li>
              <li className="body-large flex items-start">
                <span className="text-bronze mr-4">•</span>
                <span>Leaders who are strong on vision but need the operational backbone to match their ambition</span>
              </li>
            </ul>
            
            <div className="text-center mt-12">
              <p className="body-large text-charcoal font-semibold">
                The shift: you've validated your idea, now you need infrastructure to grow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Advance Works */}
      <section id="how-advance-works" className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="headline-medium mb-8">
              How It Works
            </h2>
          </div>
          
          <div className="max-w-6xl mx-auto space-y-12">
            {[
              {
                step: "1",
                title: "Choose Your Build Path",
                description: "Select the investment plan that fits your pace — full, milestone, installment, or starter. Every path leads to a tangible business outcome, not just advice."
              },
              {
                step: "2", 
                title: "Secure Your Build Window",
                description: "Your 10-week engagement is scheduled within two weeks of payment. Full-pay founders start in as little as 7 days. We lock your roadmap and assign your dedicated pod."
              },
              {
                step: "3",
                title: "Get Your Welcome Package", 
                description: "You receive access to your digital onboarding hub, discovery workbook, and readiness checklist. These inputs allow us to tailor the build precisely to your business DNA."
              },
              {
                step: "4",
                title: "Kick Off With Your Executive Team",
                description: "Your lead point of contact coordinates a dedicated team of strategist, CTO, designer, and operations leads. We align on your goals and immediately begin execution."
              },
              {
                step: "5",
                title: "Build Week by Week",
                description: "Each week produces concrete deliverables — from business model design to CRM automation, from visual identity to AI integration. By Week 10, your company is fully operational, branded, and scale-ready."
              },
              {
                step: "6", 
                title: "Launch & Handover",
                description: "We deliver your brand system, data room, and AI co-pilot, along with full training. You exit with a company designed to run without your constant involvement."
              }
            ].map((item, index) => (
              <div key={index} className="editorial-card hover-lift">
                <div className="flex flex-col md:flex-row md:items-start md:space-x-8">
                  <div className="flex-shrink-0 mb-6 md:mb-0">
                    <div className="w-16 h-16 bg-bronze text-white rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold">{item.step}</span>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="headline-small text-charcoal mb-4">{item.title}</h3>
                    <p className="body-large text-graphite">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding bg-ivory">
        <div className="container mx-auto">
          <h2 className="headline-medium text-center mb-12">
            What you'll leave with in 10 weeks
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              "Clear business model and revenue framework",
              "Operational workflows and SOPs", 
              "Systems and automations to reduce manual work",
              "Customer journey and digital sales funnel set up",
              "Validated Brand identity",
              "Custom website",
              "Investor/partner-ready data room and pitch deck",
              "AI co-pilot trained on your materials to extend your capacity"
            ].map((item, index) => (
              <div key={index} className="editorial-card hover-lift">
                <div className="flex items-start">
                  <span className="text-bronze text-xl mr-4">✓</span>
                  <p className="body-medium text-charcoal">{item}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Readiness Quiz */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="headline-medium mb-8">
              Readiness Quiz
            </h2>
            <p className="subhead-medium text-charcoal mb-6">
              5 minutes to clarity
            </p>
            <p className="body-large text-graphite mb-12">
              Find out where your business is strong, where the gaps are, and what to build next. You'll get a score, a snapshot, and a personalized plan.
            </p>
            <a href="/quiz">
              <Button className="btn-primary">
                Start the readiness check
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What You Walk Away With - Detailed Value */}
      <section className="section-padding bg-ivory">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="headline-medium mb-8">
              What You Walk Away With
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="body-large text-charcoal font-semibold mb-4">
                This isn't theory. This isn't a course.
              </p>
              <p className="body-large text-graphite mb-8">
                At the end of 10 weeks, you walk away with a full business infrastructure, built with you, not for you.
              </p>
              <p className="body-large text-charcoal">
                <strong>And here's the kicker:</strong> if you hired separate agencies, consultants, and dev shops to deliver the same work, you'd be looking at <span className="text-bronze font-bold">over $140,000 in fees.</span>
              </p>
              <p className="subhead-medium text-bronze mt-4">
                With Advance, you get it:
              </p>
            </div>
          </div>

          <div className="max-w-6xl mx-auto space-y-12">
            {/* Strategy & Business Architecture */}
            <div className="editorial-card hover-lift">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                <div className="lg:w-1/3 mb-6 lg:mb-0">
                  <div className="label-text text-bronze mb-2">WEEKS 1–4</div>
                  <h3 className="headline-small text-charcoal mb-4">Strategy & Business Architecture</h3>
                  <div className="text-2xl font-bold text-bronze">$32,500 Value</div>
                </div>
                <div className="lg:w-2/3">
                  <ul className="space-y-3 mb-6">
                    {[
                      'Founder Vision Map & KPI Dashboard',
                      'Business Model & Revenue Plan', 
                      'Customer Journey Blueprint',
                      'Offer Suite & Pricing Ladder',
                      'Investor-Ready Messaging & Positioning'
                    ].map((item, idx) => (
                      <li key={idx} className="body-medium flex items-start">
                        <span className="text-bronze mr-3">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="body-medium text-graphite italic">
                    Finally see your whole business on one page — with a model designed to scale.
                  </p>
                </div>
              </div>
            </div>

            {/* Brand & Digital Presence */}
            <div className="editorial-card hover-lift">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                <div className="lg:w-1/3 mb-6 lg:mb-0">
                  <div className="label-text text-bronze mb-2">WEEKS 5–6</div>
                  <h3 className="headline-small text-charcoal mb-4">Brand & Digital Presence</h3>
                  <div className="text-2xl font-bold text-bronze">$30,000 Value</div>
                </div>
                <div className="lg:w-2/3">
                  <ul className="space-y-3 mb-6">
                    {[
                      'Visual Brand Identity (logo, palette, typography, design system)',
                      'Messaging & Copy Kit',
                      '1–5 Page SEO-Ready Website',
                      'Sales Assets',
                      'Branded Social'
                    ].map((item, idx) => (
                      <li key={idx} className="body-medium flex items-start">
                        <span className="text-bronze mr-3">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="body-medium text-graphite italic">
                    Look like the company you've always wanted to be — investor-ready, premium, and trustworthy.
                  </p>
                </div>
              </div>
            </div>

            {/* Systems & Operations */}
            <div className="editorial-card hover-lift">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                <div className="lg:w-1/3 mb-6 lg:mb-0">
                  <div className="label-text text-bronze mb-2">WEEKS 7–8</div>
                  <h3 className="headline-small text-charcoal mb-4">Systems & Operations</h3>
                  <div className="text-2xl font-bold text-bronze">$33,000 Value</div>
                </div>
                <div className="lg:w-2/3">
                  <ul className="space-y-3 mb-6">
                    {[
                      'CRM Setup + Automations',
                      'Lead Capture & Sales Funnel',
                      'Analytics Dashboard', 
                      'SOP Playbook & Delegation System',
                      'Growth Roadmap + Campaign Calendar'
                    ].map((item, idx) => (
                      <li key={idx} className="body-medium flex items-start">
                        <span className="text-bronze mr-3">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="body-medium text-graphite italic">
                    Stop operating from your inbox. Your business runs itself in the background while you focus on growth.
                  </p>
                </div>
              </div>
            </div>

            {/* AI Co-Pilot & Automation Layer */}
            <div className="editorial-card hover-lift">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                <div className="lg:w-1/3 mb-6 lg:mb-0">
                  <div className="label-text text-bronze mb-2">WEEK 9</div>
                  <h3 className="headline-small text-charcoal mb-4">AI Co-Pilot & Automation Layer</h3>
                  <div className="text-2xl font-bold text-bronze">$23,000 Value</div>
                </div>
                <div className="lg:w-2/3">
                  <ul className="space-y-3 mb-6">
                    {[
                      'Custom GPT Co-Pilot trained on your business',
                      'Prompt Library (get usable drafts in minutes)',
                      'Automation Workflows for marketing, reporting, and customer support'
                    ].map((item, idx) => (
                      <li key={idx} className="body-medium flex items-start">
                        <span className="text-bronze mr-3">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="body-medium text-graphite italic">
                    It's like hiring a team of five — without adding payroll.
                  </p>
                </div>
              </div>
            </div>

            {/* Launch & Scale */}
            <div className="editorial-card hover-lift">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                <div className="lg:w-1/3 mb-6 lg:mb-0">
                  <div className="label-text text-bronze mb-2">WEEK 10</div>
                  <h3 className="headline-small text-charcoal mb-4">Launch & Scale</h3>
                  <div className="text-2xl font-bold text-bronze">$22,500 Value</div>
                </div>
                <div className="lg:w-2/3">
                  <ul className="space-y-3 mb-6">
                    {[
                      'Go-to-Market Plan & Launch Execution',
                      'Growth Roadmap (12 months)',
                      'Starter Data Room (investor-ready assets organized)',
                      'Sales Deck & Pitch Template',
                      '90-Day Implementation Calendar',
                      '6 Months of AI Co-Pilot Hosting'
                    ].map((item, idx) => (
                      <li key={idx} className="body-medium flex items-start">
                        <span className="text-bronze mr-3">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="body-medium text-graphite italic">
                    You leave not with a plan, but with a fully launched business that runs like a company, not a side hustle.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ROI Summary */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="editorial-card bg-white border-2 border-bronze">
              <div className="text-center">
                <h3 className="headline-small text-charcoal mb-8">The ROI</h3>
                <div className="grid md:grid-cols-3 gap-8 mb-8">
                  <div>
                    <div className="text-3xl font-bold text-graphite mb-2">Market Value</div>
                    <div className="text-2xl text-bronze font-bold">$141,500+</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-graphite mb-2">Your Investment</div>
                    <div className="text-2xl text-bronze font-bold">$22,500</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-graphite mb-2">Your ROI</div>
                    <div className="text-2xl text-teal font-bold">5–6x</div>
                  </div>
                </div>
                <p className="body-medium text-graphite mb-8">
                  <strong>5–6x in immediate build value</strong> — before a single new customer lands.
                </p>
                <div className="border-t border-bronze-light pt-8">
                  <p className="body-large text-charcoal font-semibold mb-4">
                    Advance isn't about buying time with a coach.
                  </p>
                  <p className="body-large text-graphite mb-8">
                    It's about compressing years of trial and error into 10 weeks of world-class execution.
                  </p>
                  <p className="subhead-medium text-bronze mb-8">
                    Ready to step out of the weeds and into the CEO seat?
                  </p>
                  <a href="/book-call">
                    <Button className="btn-primary">
                      Book Your Strategy Call
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Investment */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <h2 className="headline-medium mb-8">
              Choose your build path
            </h2>
            <p className="body-large text-graphite max-w-3xl mx-auto mb-8">
              Decide how you want to structure your investment — every option delivers the same 10-week transformation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {pricingOptions.map((option, index) => (
              <div 
                key={index}
                className={`editorial-card hover-lift ${
                  option.highlight 
                    ? 'border-bronze-strong bg-bronze text-white transform scale-102' 
                    : 'bg-white'
                }`}
              >
                <div className="text-center mb-6">
                  <div className={`text-4xl mb-4 ${option.highlight ? 'text-white' : 'text-bronze'}`}>
                    {option.icon}
                  </div>
                  <h3 className={`headline-small mb-4 ${option.highlight ? 'text-white' : 'text-charcoal'}`}>
                    {option.title}
                  </h3>
                  <div className={`text-2xl font-bold mb-2 ${option.highlight ? 'text-white' : 'text-bronze'}`}>
                    {option.price}
                  </div>
                  {option.savings && (
                    <div className={`label-text mb-4 ${option.highlight ? 'text-white opacity-90' : 'text-teal'}`}>
                      {option.savings}
                    </div>
                  )}
                  <p className={`body-medium mb-6 ${option.highlight ? 'text-white opacity-90' : 'text-graphite'}`}>
                    {option.description}
                  </p>
                </div>

                <div className="text-left mb-6">
                  <ul className="space-y-3">
                    {option.features?.map((feature, idx) => (
                      <li key={idx} className={`body-small flex items-start ${option.highlight ? 'text-white opacity-90' : 'text-charcoal'}`}>
                        <span className={`mr-3 ${option.highlight ? 'text-white' : 'text-bronze'}`}>•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`p-4 rounded-lg mb-6 ${
                  option.highlight ? 'bg-white bg-opacity-20' : 'bg-ivory'
                }`}>
                  <p className={`body-small italic ${option.highlight ? 'text-white' : 'text-charcoal'}`}>
                    👉 {option.tagline}
                  </p>
                </div>

                <div className="text-center">
                  <a href="/get-started">
                    <Button 
                      className={option.highlight ? "btn-secondary-inverted" : "btn-primary"}
                    >
                      Secure This Build Path
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-16">
            <p className="body-large text-graphite max-w-3xl mx-auto">
              No matter which path you choose, you leave with a company ready to scale beyond you.
            </p>
          </div>
        </div>
      </section>

      {/* Results & Testimonials */}
      <section className="section-padding bg-ivory">
        <div className="container mx-auto">
          {/* Metrics */}
          <div className="text-center mb-24">
            <h2 className="headline-medium mb-16">Founder Outcomes That Matter</h2>
            <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
              <div className="text-center">
                <div className="text-6xl font-bold text-bronze mb-4">80%</div>
                <p className="subhead-small text-charcoal mb-3">Launch revenue-ready within 10 weeks</p>
                <p className="body-small text-graphite">Most founders leave the Accelerator with offers, systems, and assets ready to sell immediately.</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold text-bronze mb-4">50%</div>
                <p className="subhead-small text-charcoal mb-3">Close their first paying clients in under 60 days</p>
                <p className="body-small text-graphite">From zero traction to first sales faster than most accelerators even begin.</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold text-bronze mb-4">90%</div>
                <p className="subhead-small text-charcoal mb-3">Actively using their AI co-pilot post-engagement</p>
                <p className="body-small text-graphite">Automation + AI become a daily growth engine, not a gimmick.</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold text-bronze mb-4">8.5+</div>
                <p className="subhead-small text-charcoal mb-3">Average NPS from founders</p>
                <p className="body-small text-graphite">Consistently rated as a game-changing experience worth recommending to peers.</p>
              </div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="space-y-16 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="editorial-card hover-lift">
                <div className="grid md:grid-cols-3 gap-12 items-center">
                  <div>
                    <div className="label-text text-bronze mb-3">{testimonial.type}</div>
                    <h3 className="subhead-medium text-charcoal">{testimonial.founder}</h3>
                    <p className="body-small text-graphite">{testimonial.company}</p>
                    <p className="label-text text-graphite">{testimonial.location}</p>
                  </div>
                  <div className="md:col-span-2">
                    <blockquote className="body-large text-charcoal mb-6 italic font-light">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="label-text text-teal font-semibold">
                      {testimonial.result}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <h2 className="headline-medium text-center mb-20">
            How it works
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {[
                "Simple, founder-paced onboarding",
                "Choose your build path", 
                "Secure your build window (2 weeks from today)",
                "Get your Welcome Package + Discovery Workbook",
                "Kick off with your dedicated team"
              ].map((step, index) => (
                <div key={index} className="flex items-center space-x-8">
                  <div className="w-16 h-16 bg-bronze text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                    {index + 1}
                  </div>
                  <p className="body-large text-charcoal">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Copper & Vine */}
      <section className="section-padding bg-ivory">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="headline-medium mb-8">
              About Copper & Vine
            </h2>
            <p className="subhead-large text-charcoal mb-8">
              Built by founders, for founders.
            </p>
            <p className="body-medium text-graphite mb-8 max-w-3xl mx-auto">
              Copper & Vine is a creative technology studio based in Washington, DC — founded on the belief that bold visions deserve world-class execution.
            </p>
            <p className="body-medium text-graphite mb-8 max-w-3xl mx-auto">
              For more than 20 years, our team has helped founders, startups, and enterprises turn ideas into scalable companies. We've led global brand initiatives, architected digital systems, and guided founders through the messy middle of growth — from early traction to sustainable scale.
            </p>
            <div className="mb-12 max-w-3xl mx-auto">
              <p className="body-large text-charcoal font-semibold mb-2">
                We don't just help you build a business.
              </p>
              <p className="body-large text-charcoal font-semibold">
                We help you build a company that can outlast you.
              </p>
            </div>
            <a href="https://www.coppervine.io/about" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="btn-secondary">
                Learn More About Our Story
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Compliance & Governance */}
      <section className="section-padding bg-ivory">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="headline-medium mb-8">
              Built to Last
            </h2>
          </div>
          
          <div className="max-w-6xl mx-auto space-y-6">
            {[
              {
                title: "Secure & Reliable Systems",
                description: "Tools configured specifically for your business, not off-the-shelf hacks."
              },
              {
                title: "Clear Playbooks",
                description: "SOPs and documentation so your company runs smoothly even when you step back."
              },
              {
                title: "Risk Reduction", 
                description: "Systems designed to eliminate bottlenecks, cut errors, and keep revenue predictable."
              },
              {
                title: "Compliance-Ready",
                description: "Optional support for HIPAA, GDPR, or SOC2 so you scale with confidence."
              }
            ].map((item, index) => (
              <div key={index} className="editorial-card hover-lift">
                <div className="flex items-start">
                  <span className="text-bronze text-xl mr-4">✓</span>
                  <div>
                    <h3 className="body-large font-semibold text-charcoal mb-2">{item.title}:</h3>
                    <p className="body-medium text-graphite">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="text-center mt-12">
              <div className="max-w-3xl mx-auto">
                <p className="body-large text-charcoal font-semibold mb-2">
                  We don't just build momentum
                </p>
                <p className="body-large text-charcoal font-semibold">
                  We build infrastructure that grows with you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="headline-medium mb-8">
              What We Build With You
            </h2>
          </div>
          
          <div className="max-w-6xl mx-auto space-y-6">
            {[
              "Business Model & Growth Roadmap — A clear revenue plan, pricing structure, and KPI dashboard tailored to your goals.",
              "Customer Journey & Offer Suite — Mapped pathways from lead to loyal client, plus tiered offers that increase lifetime value.",
              "Brand Identity & Messaging System — Professional visual identity, voice, and positioning that elevate trust with investors and customers.",
              "Website & Digital Presence — A polished 1–5 page SEO-ready site with integrated lead funnel and analytics.",
              "CRM & Automated Operations — Client management, onboarding, invoicing, and marketing flows running seamlessly in the background.",
              "SOP Playbook & Training Hub — Clear documentation and handover tools so your systems are repeatable and scalable.",
              "Custom AI Co-Pilot — Your own GPT-based assistant, trained on your business, that drafts content, manages tasks, and saves you hours.",
              "Launch Assets & Investor Pack — Sales deck, pitch-ready data room, and launch campaign strategy to accelerate traction and funding."
            ].map((item, index) => (
              <div key={index} className="editorial-card hover-lift">
                <div className="flex items-start">
                  <span className="text-bronze text-xl mr-4">✓</span>
                  <p className="body-medium text-charcoal">{item}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="headline-medium text-center mb-20">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="editorial-card hover-lift overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full text-left focus:outline-none focus:ring-2 focus:ring-bronze focus:ring-opacity-50 rounded-lg"
                  >
                    <div className="flex items-center justify-between p-6">
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl">{faq.emoji}</span>
                        <h3 className="subhead-medium text-bronze">
                          {faq.question}
                        </h3>
                      </div>
                      <div className={`transform transition-transform duration-300 ${
                        openFAQ === index ? 'rotate-180' : 'rotate-0'
                      }`}>
                        <svg 
                          className="w-6 h-6 text-bronze" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M19 9l-7 7-7-7" 
                          />
                        </svg>
                      </div>
                    </div>
                  </button>
                  
                  <div className={`transition-all duration-300 ease-in-out ${
                    openFAQ === index 
                      ? 'max-h-screen opacity-100' 
                      : 'max-h-0 opacity-0'
                  } overflow-hidden`}>
                    <div className="px-6 pb-6">
                      <div className="pl-12">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final Conversion Block */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="headline-medium mb-12">
              Start anytime. Advance everything.
            </h2>
            <div className="flex flex-col sm:flex-row gap-8 justify-center">
              <a href="/book-call">
                <Button className="btn-primary text-lg px-16 py-4">
                  Book Your Strategy Call
                </Button>
              </a>
              <a href="/quiz">
                <Button variant="outline" className="btn-secondary text-lg px-16 py-4">
                  Take the Readiness Quiz
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-dark py-20">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="text-white mb-6">
                <span className="headline-small" style={{ fontFamily: 'Canela, Georgia, serif' }}>ADVANCE</span>
                <span className="label-text text-white opacity-60 block mt-2">by Copper & Vine</span>
              </div>
              <p className="body-small text-white opacity-60">
                Where visionary founders meet world-class execution.
              </p>
            </div>
            
            <div>
              <h3 className="subhead-medium text-white mb-6">Quick Links</h3>
              <ul className="space-y-3">
                <li><a href="#home" className="footer-link body-small">Home</a></li>
                <li><a href="#quiz" className="footer-link body-small">Readiness Quiz</a></li>
                <li><a href="#case-studies" className="footer-link body-small">Case Studies</a></li>
                <li><a href="#about" className="footer-link body-small">About</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="subhead-medium text-white mb-6">Contact</h3>
              <p className="body-small text-white opacity-60 mb-2">Washington, DC</p>
              <p className="body-small text-white opacity-60">hello@copperandvine.com</p>
            </div>
            
            <div>
              <h3 className="subhead-medium text-white mb-6">Legal</h3>
              <ul className="space-y-3">
                <li><a href="#privacy" className="footer-link body-small">Privacy Policy</a></li>
                <li><a href="#terms" className="footer-link body-small">Terms of Service</a></li>
                <li><a href="#cookies" className="footer-link body-small">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white border-opacity-20 mt-16 pt-12 text-center">
            <p className="body-small text-white opacity-60">
              © 2024 Copper & Vine. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;